import { action, flow, makeObservable, observable } from 'mobx';
import { GlobalStore, UserStore } from '@roc/feature-app-core';
import { SubmitAppraisalService } from '../services/submitAppraisalService';
import { SubmitAppraisalBaseStore } from './submitAppraisalBaseStore';
import { LoanSubType, SegmentTrackerEvent, segmentAnalytics, LOAN_FUNDER, isNil, isNotBlank } from '@roc/feature-utils';
import { SelectBorrowersStore } from '@roc/feature-borrowers';
import { BorrowerFormStore } from '@roc/feature-borrowers';
import { LoanParticipantsStore } from '@roc/feature-loan-participants';

const appraisalInformation = {
  fields: {
    selectedLoanSubType: {
      value: null,
      error: null,
      rule: '',
    },
    __isValidNoInteriorAccessComment__: {
      value: null,
      error: null,
      rule: '',
    },
    useCode: {
      value: '',
      error: null,
      rule: 'required',
      message: 'This field is required.',
    },
    address: {
      value: '',
      error: null,
      rule: 'required',
      message: 'This field is required.',
    },
    __isAddressInCorrectFormat__: {
      value: true,
      error: null,
      rule: 'accepted',
      message: 'Invalid Address Format',
    },
    streetNumber: {
      value: '',
      error: null,
      rule: 'required',
      message: 'This field is required.',
    },
    streetName: {
      value: '',
      error: null,
      rule: 'required',
      message: 'This field is required.',
    },
    city: {
      value: '',
      error: null,
      rule: 'required',
      message: 'This field is required.',
    },
    state: {
      value: '',
      error: null,
      rule: 'required',
      message: 'This field is required.',
    },
    zipCode: {
      value: '',
      error: null,
      rule: 'required',
      message: 'This field is required.',
    },
    latitude: {
      value: 0,
      error: null,
      rule: '',
      message: '',
    },
    longitude: {
      value: 0,
      error: null,
      rule: '',
      message: '',
    },
    propertyOwnership: {
      value: '',
      error: null,
      rule: [{ required_if: ['wholesaleLender', LOAN_FUNDER] }],
      message: 'This field is required.',
    },
    propertySourcing: {
      value: '',
      error: null,
      rule: [{ required_if: ['propertyOwnership', 'Purchase'] }],
      message: 'This field is required.',
      attribute: 'property sourcing',
    },
    wholesaleAmount: {
      value: '',
      error: null,
      rule: [
        { required_if: ['propertySourcing', 'Wholesaler'] },
        'min:0',
        'max:1000000000000',
      ],
      attribute: 'wholesale amount',
    },
    purchasePrice: {
      value: '',
      error: null,
      rule: [{ required_if: ['wholesaleLender', LOAN_FUNDER], min: 0, max: 1000000000000 }],
      attribute: 'purchase price',
    },
    totalRenovationBudget: {
      value: '',
      error: null,
      rule: [{ required_if: ['wholesaleLender', LOAN_FUNDER], min: 0, max: 1000000000000 }],
      attribute: 'renovation budget',
    },
    scopeOfWorkFile: {
      value: null,
      error: null,
      rule: '',
    },
    afterRepairValue: {
      value: '',
      error: null,
      rule: [{ required_if: ['wholesaleLender', LOAN_FUNDER], min: 0, max: 1000000000000 }],
      attribute: 'after repair value',
    },
    exitStrategy: {
      value: '',
      error: null,
      rule: [{ required_if: ['wholesaleLender', LOAN_FUNDER] }],
      message: 'This field is required.',
    },
    purchaseDate: {
      value: null,
      error: null,
      rule: [{ required_if: ['propertyOwnership', 'Owned By Borrower'] }],
      message: 'This field is required.',
    },
    sunkCost: {
      value: '',
      error: null,
      rule: [{ required_if: ['propertyOwnership', 'Owned By Borrower'] }],
      message: 'This field is required.',
    },
    renovationRemaining: {
      value: '',
      error: null,
      rule: [{ required_if: ['propertyOwnership', 'Owned By Borrower'] }],
      message: 'This field is required.',
    },
    payoffAmount: {
      value: '',
      error: null,
      rule: [{ required_if: ['propertyOwnership', 'Owned By Borrower'] }],
      message: 'This field is required.',
    },
    // Appraisal Details
    submitRushedAppraisal: {
      value: '',
      error: null,
      rule: 'required',
      message: 'This field is required.',
    },
    // Borrower Information
    borrower: {
      value: null,
      error: null,
      rule: [{ required_if: ['wholesaleLender', LOAN_FUNDER] }],
      message: 'This field is required.',
    },
    // originator info
    originatorId: {
      value: null,
      error: null
    },
    wholesaleLender: {
      value: LOAN_FUNDER,
      error: null,
      rule: 'required',
      message: 'This field is required.',
    },
    wholesaleLenderSearch: {
      value: '',
      error: '',
      rule: '',
    },
    county: {
      value: '',
      error: null,
      rule: [{ required_unless: ['wholesaleLender', LOAN_FUNDER] }],
      message: 'This field is required.',
    },
    occupancyType: {
      value: '',
      error: null,
      rule: [{ required_unless: ['wholesaleLender', LOAN_FUNDER] }],
      message: 'This field is required.',
    },
    transactionType: {
      value: '',
      error: null,
      rule: [{ required_unless: ['wholesaleLender', LOAN_FUNDER] }],
      message: 'This field is required.',
    },
    loanType: {
      value: '',
      error: null,
      rule: [{ required_unless: ['wholesaleLender', LOAN_FUNDER] }],
      message: 'This field is required.',
    },
    loanExternalNumber: {
      value: '',
      error: null,
      rule: [{ required_unless: ['wholesaleLender', LOAN_FUNDER] }],
      message: 'This field is required.',
    },
    fullName: {
      value: '',
      error: null,
      rule: [{ required_unless: ['wholesaleLender', LOAN_FUNDER] }],
      message: 'This field is required.',
    },
    cellPhone: {
      value: '',
      error: null,
      rule: [
        { required_unless: ['wholesaleLender', LOAN_FUNDER] },
        'regex:/^\\d{3}-?\\d{3}-?\\d{4}$/',
      ],
      message: 'This field is required.',
    },
    emailAddress: {
      value: '',
      error: null,
      rule: [
        {
          required_unless: ['wholesaleLender', LOAN_FUNDER],
          email: true
        }
      ],
      message: 'You must enter a valid email address',
    },
    appraisalType: {
      value: '',
      error: null,
      rule: [{ required_unless: ['wholesaleLender', LOAN_FUNDER] }],
      message: 'This field is required.',
    },
    appraisalType1: {
      value: '',
      error: null,
      rule: '',
    },
    appraisalType2: {
      value: '',
      error: null,
      rule: '',
    },
    appraisalType3: {
      value: '',
      error: null,
      rule: '',
    },
    clientComments: {
      value: '',
      error: null,
      rule: '',
    }
  },
  meta: {
    isValid: false,
    error: null,
  },
};

export class SubmitAppraisalStore extends SubmitAppraisalBaseStore {
  submitAppraisalService: SubmitAppraisalService;
  loanParticipantsStore: LoanParticipantsStore;
  userStore: UserStore;
  selectBorrowersStore: SelectBorrowersStore;
  errorMessage: string;

  constructor(globalStore: GlobalStore, userStore: UserStore) {
    super(appraisalInformation, globalStore);
    this.userStore = userStore;
    this.submitAppraisalService = new SubmitAppraisalService();
    this.loanParticipantsStore = new LoanParticipantsStore(this.globalStore);
    this.isClientPortal = true;
    this.selectBorrowersStore = new SelectBorrowersStore(
      globalStore,
      new BorrowerFormStore(globalStore),
      undefined,
      undefined,
      x => this.handleBorrowerChange(x),
      undefined,
      () => new BorrowerFormStore(globalStore)
    );
    this.errorMessage = null;

    makeObservable(this, {
      loadStore: flow,
      submitAppraisal: flow,
      errorMessage: observable,
      doesPipelineLoanWithSameAddressExists: flow,
      resetAddress: action,
    });
  }

  *loadStore() {
    yield this.loanParticipantsStore.fetchLoanParticipants();
    const { userId } = this.userStore.userInformation;
    if (this.globalStore.userFeatures?.isLenderOriginator) {
      this.onFieldChange('originatorId', userId);
    }
  }

  handleSubmitStabilizedOrTerm() {
    const { selectedLoanSubType } = this.getFormValues();
    const isLoanSubTypeStabilizedOrTerm =
      selectedLoanSubType === LoanSubType.STABILIZED_BRIDGE ||
      selectedLoanSubType === LoanSubType.SINGLE_PROPERTY ||
      selectedLoanSubType === LoanSubType.RENTAL_PORTFOLIO;
    if (isLoanSubTypeStabilizedOrTerm) {
      this.setFormFieldValue('afterRepairValue', 0);
      this.setFormFieldValue('exitStrategy', "None");
      this.setFormFieldValue('totalRenovationBudget', 0);
      this.setFormFieldValue('renovationRemaining', 0);
      this.setFormFieldValue('sunkCost', 0);
    }
  }

  *submitAppraisal() {
    this.handleSubmitStabilizedOrTerm();
    this.runFormValidationWithMessage();

    const formValues = this.getFormValues();

    if (isNil(formValues?.borrower?.cellPhone) || formValues?.borrower?.cellPhone === "") {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'A phone number is required for the borrower.'
      });
      return;
    }

    if (this.form.meta.isValid) {
      try {
        const formValues = this.getFormValues();
        const formInformation = this.parseFormInformation(formValues);
        const scopeOfWorkFile = this.getScopeOfWorkFile(formValues);
        const response = yield this.submitAppraisalService.submitAppraisal(
          formInformation,
          scopeOfWorkFile
        );
        this.isSubmitted = true;
        this.errorMessage = null;
        if (isNotBlank(response?.data?.error?.error)) {
          if (response?.data?.error?.error == 'FAILED_DEPENDENCY') {
            this.errorMessage = 'Unable to submit appraisal order.'
            this.globalStore.notificationStore.showErrorNotification({
              message: response?.data?.error?.message,
            });
          } else {
            this.globalStore.notificationStore.showErrorNotification({
              message: 'Error while submitting appraisal order.',
            });
          }
        }
        else {
          this.submittedAppraisalId = response.data?.data?.propertyAppraisalId;
          this.globalStore.notificationStore.showSuccessNotification({
            message: 'Appraisal order submitted successfully.',
          });
          segmentAnalytics.trackEvent({
            name: SegmentTrackerEvent.APPRAISAL_SUBMISSION,
            userName: this.userStore?.userInformation?.username,
          });
        }
      } catch (error) {
        console.log('error', error);
        this.globalStore.notificationStore.showErrorNotification({
          message: 'Error while submitting appraisal order.',
        });
      }
    }

  }

  saveAddressField(address: string, addressDetails, geometryDetails) {
    super.saveAddressField(address, addressDetails, geometryDetails);
    this.doesPipelineLoanWithSameAddressExists();
  }

  *doesPipelineLoanWithSameAddressExists() {
    try {
      this.errorMessage = null;
      const formValues = this.getFormValues();
      if (formValues.__isAddressInCorrectFormat__) {
        const response = yield this.submitAppraisalService.doesPipelineLoanWithSameAddressExists(formValues.streetNumber, formValues.streetName, formValues.aptNumber, formValues.city, formValues.state, formValues.zipCode);
        if (isNotBlank(response?.data?.error?.error) && response?.data?.error?.error == 'FAILED_DEPENDENCY') {
          this.errorMessage = response?.data?.error?.message;
        }
      }
    } catch (error) {
      console.log('error', error);
    }
  }

  resetAddress() {
    const oldSelectedLoanSubType = this.selectedLoanSubType;
    this.reset();
    this.selectedLoanSubType = oldSelectedLoanSubType;
  }

  reset() {
    super.reset();
    this.selectBorrowersStore.reset();
    this.errorMessage = null;
  }
}
